import { Modal } from "pepsico-ds";
import PropTypes from "prop-types";
import { useMemo } from "react";
import JsonFormsWrapper from "../../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { FeatureToggle, useFeatureToggle } from "../../../utils/featureToggle";
import { keywordFormSchema, keywordUISchema } from "./keywordFormSchema";
import "./keywordManagement.scss";
import { formatValidationErrors } from "./validationErrors";

const KeywordModal = ({
  isOpen,
  mode,
  formData,
  errors = [],
  submitAttempted = false,
  isLoading,
  onClose,
  onSubmit,
  onFormChange,
}) => {
  const showKeywordCategory = useFeatureToggle(FeatureToggle.showKeywordCategory);

  const activeFormSchema = useMemo(() => {
    if (showKeywordCategory) return keywordFormSchema;
    const propertiesWithoutCategory = Object.fromEntries(
      Object.entries(keywordFormSchema.properties).filter(
        ([key]) => key !== "category"
      )
    );
    return { ...keywordFormSchema, properties: propertiesWithoutCategory };
  }, [showKeywordCategory]);

  const activeUISchema = useMemo(() => {
    if (showKeywordCategory) return keywordUISchema;
    return {
      ...keywordUISchema,
      elements: keywordUISchema.elements.map((el) => {
        if (el.type !== "HorizontalLayout") return el;
        return {
          ...el,
          elements: el.elements.filter(
            (child) => child.scope !== "#/properties/category"
          ),
        };
      }),
    };
  }, [showKeywordCategory]);

  if (!isOpen) return null;

  const title =
    mode === "delete"
      ? "Delete Keyword"
      : mode === "edit"
        ? "Edit Keyword"
        : "Add Keyword";
  const primaryButtonText =
    mode === "delete" ? "Confirm" : mode === "edit" ? "Update" : "Create";

  const handlePrimaryAction = async () => {
    await onSubmit();
  };

  const handleFormDataChange = (data, nextErrors) => {
    onFormChange(data, nextErrors);
  };

  const showValidationSummary =
    mode !== "delete" && submitAttempted && errors.length > 0;
  const formattedErrors = showValidationSummary
    ? formatValidationErrors(errors)
    : [];

  return (
    <Modal
      showModal={isOpen}
      type="S"
      title={title}
      onCloseModal={onClose}
      primaryButtonProps={{
        size: "medium",
        text: primaryButtonText,
        variant: mode === "delete" ? "danger" : "primary",
        onClick: handlePrimaryAction,
        disabled: isLoading,
        loading: isLoading,
      }}
      secondaryButtonProps={{
        size: "medium",
        text: "Cancel",
        variant: "secondary",
        onClick: onClose,
        disabled: isLoading,
      }}
      showTertiaryButton={false}
      showLink={false}
      className="keyword-modal"
    >
      <div className="keyword-modal-content">
        {mode === "delete" ? (
          <div className="delete-confirmation">
            <p>
              Are you sure you want to delete the keyword &quot;{formData?.name}
              &quot;?
            </p>
            <p className="warning-text">This action cannot be undone.</p>
          </div>
        ) : (
          <>
            {showValidationSummary && (
              <div
                className="validation-summary"
                role="alert"
                aria-label="Form validation errors"
              >
                <p className="validation-summary-title">
                  Please fix the following before submitting:
                </p>
                <ul>
                  {formattedErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
            <JsonFormsWrapper
              jsonschema={activeFormSchema}
              uischema={activeUISchema}
              data={formData}
              onChange={handleFormDataChange}
              readonly={isLoading}
              config={{ showValidation: submitAttempted }}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

KeywordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["add", "edit", "delete"]).isRequired,
  formData: PropTypes.object.isRequired,
  errors: PropTypes.array,
  submitAttempted: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onFormChange: PropTypes.func.isRequired,
};

export default KeywordModal;
